import type { Transaction } from "@/apis/gql/types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TransactionsPanelHeader } from "@/features/transactions/shared/transactions-panel-header";
import { SearchIcon, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useSearchTransactionInvoices } from "@/queries/transactions/search-invoices";
import { InvoiceMatchingForm } from "../../shared/invoice-matching-form";

export enum TransactionsPanelTabs {
  MATCH = "match",
}

type Props = {
  transactions: Transaction[];
  onRowSelectionChange: (value: Record<string, boolean>) => void;
  rowSelection: Record<string, boolean>;
  onMatch: (invoiceIds: string[]) => Promise<void>;
  onUnmatch: (invoiceIds: string[]) => Promise<void>;
  onClose: () => void;
};

export const InvoiceMatchingPanel = ({
  transactions,
  onRowSelectionChange,
  rowSelection,
  onMatch,
  onUnmatch,
  onClose,
}: Props) => {
  const { t } = useTranslation();

  const [invoicesSearch, setInvoicesSearch] = useState("");

  const transactionIds = useMemo(
    () => transactions.map((transaction) => transaction.bankTxnId),
    [transactions],
  );

  const {
    data: invoices,
    loading: invoicesLoading,
    error: invoicesError,
  } = useSearchTransactionInvoices({
    transactions,
    page: 1,
    limit: 50,
    offset: 0,
    global: invoicesSearch,
  });

  return (
    <div className="h-full rounded-md border">
      <div className="flex flex-col h-full">
        <div className="flex-1 flex flex-col gap-2 pt-4 h-full">
          <div className="px-4 flex items-start gap-2">
            <div className="flex-1">
              <TransactionsPanelHeader transactions={transactions} />
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors shrink-0"
              aria-label="Close panel"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          <Tabs
            className="flex-1 flex flex-col"
            defaultValue={TransactionsPanelTabs.MATCH}
          >
            <TabsList className="px-4">
              <TabsTrigger
                value={TransactionsPanelTabs.MATCH}
                className="gap-2"
              >
                <SearchIcon className="w-4 h-4" />
                {t("transactions.accounting.panel.tabs.match.title")}
              </TabsTrigger>
            </TabsList>
            <TabsContent value={TransactionsPanelTabs.MATCH} className="flex-1">
              <InvoiceMatchingForm
                transactionIds={transactionIds}
                onRowSelectionChange={onRowSelectionChange}
                rowSelection={rowSelection}
                invoices={invoices || []}
                loading={invoicesLoading}
                error={invoicesError}
                onMatch={handleMatch}
                onUnmatch={onUnmatch}
                rowPinningOnMatch={false}
                onInvoicesSearchChange={setInvoicesSearch}
                invoicesSearch={invoicesSearch}
                className="px-4"
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );

  async function handleMatch(invoiceIds: string[]) {
    await onMatch(invoiceIds);
  }

  function handleClose() {
    onClose();
  }
};
